import { useCallback, useState } from 'react'
import type { GlobalSearchResultItem, GlobalSearchResultType } from './global-search.interface'
import { useGlobalSearchKeyboard } from './use-global-search-keyboard.hook'

interface UseGlobalSearchParams {
  onSearch: (query: string) => void
  onResultSelect: (type: GlobalSearchResultType, item: GlobalSearchResultItem) => void
}

interface UseGlobalSearchReturn {
  isOpen: boolean
  openDialog: () => void
  handleOpenChange: (open: boolean) => void
  handleSelect: (type: GlobalSearchResultType, item: GlobalSearchResultItem) => void
}

export const useGlobalSearch = ({
  onSearch,
  onResultSelect,
}: UseGlobalSearchParams): UseGlobalSearchReturn => {
  const [isOpen, setIsOpen] = useState(false)

  const toggleOpen = useCallback(() => setIsOpen((prev) => !prev), [])

  useGlobalSearchKeyboard(toggleOpen)

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open)
      if (!open) {
        onSearch('')
      }
    },
    [onSearch]
  )

  const handleSelect = useCallback(
    (type: GlobalSearchResultType, item: GlobalSearchResultItem) => {
      onResultSelect(type, item)
      setIsOpen(false)
      onSearch('')
    },
    [onResultSelect, onSearch]
  )

  const openDialog = useCallback(() => setIsOpen(true), [])

  return { isOpen, openDialog, handleOpenChange, handleSelect }
}
